import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth';
import {
  Timestamp,
  doc,
  getDoc,
  getFirestore,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase';
import type {
  ApiResponse,
  Company,
  CreateUserData,
  CreateUserResponse,
  ResendVerifyEmailData,
  ResendVerifyEmailResponse,
  SendPasswordResetEmailData,
  SendPasswordResetEmailResponse,
  UserCompany,
  UserDocument,
} from './models';
import { getActiveMember } from './members';

const db = getFirestore();

// Create a user
export const createUser = async (data: CreateUserData): Promise<void> => {
  try {
    const createUserFunction = httpsCallable<
      CreateUserData,
      CreateUserResponse
    >(functions, 'createUser');

    await createUserFunction(data);
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Get user document by user ID
 */
export const getUser = async (
  userId: string
): Promise<ApiResponse<UserDocument>> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return { error: 'User not found' };
    }

    const data = userDoc.data();
    if (!data) {
      return { error: 'User not found' };
    }
    const user: UserDocument = {
      uid: data.uid,
      email: data.email,
      displayName: data.displayName,
      photoURL: data.photoURL,
      companyIds: data.companyIds || [],
      providerId: data.providerId,
      isActive: data.isActive,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      lastLoginAt: data.lastLoginAt?.toDate() || new Date(),
    };

    return { data: user };
  } catch (error) {
    console.error('Error getting user:', error);
    return { error: 'Failed to get user' };
  }
};

/**
 * Get companies that the user belongs to
 * This replaces the getUserCompanies function from companies.ts
 */
export const getUserCompanies = async (
  userId: string
): Promise<ApiResponse<UserCompany[]>> => {
  try {
    console.debug('[getUserCompanies] Starting for user:', userId);

    // First get the user document to get their company IDs
    const userResult = await getUser(userId);

    if (userResult.error || !userResult.data) {
      console.error('[getUserCompanies] User not found:', userResult.error);
      return { error: userResult.error || 'User not found' };
    }

    const companyIds = userResult.data.companyIds;
    console.debug('[getUserCompanies] Found company IDs:', companyIds.length);

    if (companyIds.length === 0) {
      return { data: [] };
    }

    // Fetch all companies and their member data in parallel
    const companyPromises = companyIds.map(async (companyId) => {
      try {
        const companyRef = doc(db, 'companies', companyId);
        const [companyDoc, member] = await Promise.all([
          getDoc(companyRef),
          getActiveMember(companyId, userId),
        ]);
        return { success: true, doc: companyDoc, companyId, member };
      } catch (error) {
        console.warn(
          `[getUserCompanies] Failed to fetch company ${companyId}:`,
          error
        );
        return {
          success: false,
          doc: undefined,
          companyId,
          member: null,
          error,
        };
      }
    });

    const results = await Promise.all(companyPromises);
    console.debug('[getUserCompanies] Fetch results:', {
      total: results.length,
      successful: results.filter((r) => r.success).length,
      failed: results.filter((r) => !r.success).length,
    });

    // Process only successful companies where user is an active member
    const companies: UserCompany[] = [];

    for (const result of results) {
      if (
        !result.success ||
        !result.doc ||
        !result.doc.exists() ||
        !result.member
      ) {
        continue;
      }

      const data = result.doc.data();
      if (!data) {
        continue;
      }

      companies.push({
        company: {
          id: result.doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Company,
        role: result.member.role,
      } as UserCompany);
    }

    console.debug(
      '[getUserCompanies] Successfully loaded companies:',
      companies.length
    );

    // If we got no companies but had some failures, return error
    if (companies.length === 0 && results.some((r) => !r.success)) {
      return {
        error: 'Failed to load company data. Please check your connection.',
      };
    }

    return { data: companies };
  } catch (error) {
    console.error('[getUserCompanies] Error getting user companies:', error);
    return { error: 'Failed to get user companies' };
  }
};

/**
 * Update user profile information
 */
export const updateUserProfile = async (
  userId: string,
  updates: {
    displayName?: string;
    photoURL?: string;
  }
): Promise<ApiResponse<void>> => {
  try {
    const userRef = doc(db, 'users', userId);

    const updateData: any = {
      updatedAt: Timestamp.now(),
    };

    if (updates.displayName !== undefined) {
      updateData.displayName = updates.displayName;
    }

    if (updates.photoURL !== undefined) {
      updateData.photoURL = updates.photoURL;
    }

    await updateDoc(userRef, updateData);

    return { data: void 0 };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return { error: 'Failed to update user profile' };
  }
};

/**
 * Update user's last login timestamp
 */
export const updateLastLogin = async (
  userId: string
): Promise<ApiResponse<void>> => {
  try {
    const userRef = doc(db, 'users', userId);

    await updateDoc(userRef, {
      lastLoginAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    return { data: void 0 };
  } catch (error) {
    console.error('Error updating last login:', error);
    return { error: 'Failed to update last login' };
  }
};

/**
 * Get user's primary company (first company in their companyIds array)
 */
export const getUserPrimaryCompany = async (
  userId: string
): Promise<ApiResponse<UserCompany>> => {
  try {
    const companiesResult = await getUserCompanies(userId);

    if (companiesResult.error || !companiesResult.data) {
      return { error: companiesResult.error || 'No companies found' };
    }

    const companies = companiesResult.data;

    if (companies.length === 0) {
      return { error: 'User has no companies' };
    }

    // Return the first company as primary
    return { data: companies[0] };
  } catch (error) {
    console.error('Error getting user primary company:', error);
    return { error: 'Failed to get user primary company' };
  }
};

/**
 * Check if user has access to a specific company
 */
export const hasCompanyAccess = async (
  userId: string,
  companyId: string
): Promise<boolean> => {
  try {
    const userResult = await getUser(userId);

    if (userResult.error || !userResult.data) {
      return false;
    }

    return userResult.data.companyIds.includes(companyId);
  } catch (error) {
    console.error('Error checking company access:', error);
    return false;
  }
};

export const resendVerifyEmail = async (
  data: ResendVerifyEmailData
): Promise<ApiResponse<ResendVerifyEmailResponse>> => {
  try {
    const resendVerifyEmailFunction = httpsCallable<
      ResendVerifyEmailData,
      ResendVerifyEmailResponse
    >(functions, 'resendVerifyEmail');
    const result = await resendVerifyEmailFunction(data);
    return { data: result.data as ResendVerifyEmailResponse };
  } catch (error) {
    console.error('Error resending verify email:', error);
    return { error: 'Failed to resend verify email' };
  }
};

export const sendPasswordResetEmail = async (
  data: SendPasswordResetEmailData
): Promise<ApiResponse<SendPasswordResetEmailResponse>> => {
  try {
    const sendPasswordResetEmailFunction = httpsCallable<
      SendPasswordResetEmailData,
      SendPasswordResetEmailResponse
    >(functions, 'sendPasswordResetEmail');
    const result = await sendPasswordResetEmailFunction(data);
    return { data: result.data as SendPasswordResetEmailResponse };
  } catch (error) {
    console.error('Error sending password reset email:', error);
    return { error: 'Failed to send password reset email' };
  }
};

export const deleteUser = async (): Promise<ApiResponse<void>> => {
  try {
    const deleteUserFunction = httpsCallable<void, void>(
      functions,
      'deleteUser'
    );
    await deleteUserFunction();
    return { data: void 0 };
  } catch (error) {
    console.error('Error deleting user:', error);
    return { error: 'Failed to delete user' };
  }
};

export const verifyUserEmail = async (
  searchParams: URLSearchParams
): Promise<ApiResponse<string>> => {
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  if (mode !== 'verifyEmail' || !oobCode) {
    return { error: 'Invalid verification link.' };
  }

  try {
    await applyActionCode(auth, oobCode);
    return { data: 'Your email has been verified successfully.' };
  } catch (error) {
    console.error('Error verifying email:', error);
    return { error: 'Invalid or expired verification link.' };
  }
};

export const verifyResetPasswordCode = async (
  code: string
): Promise<ApiResponse<void>> => {
  try {
    await verifyPasswordResetCode(auth, code);
    return { data: void 0 };
  } catch (error) {
    console.error('Error verifying reset code:', error);
    return { error: 'Invalid or expired password reset link.' };
  }
};

export const resetUserPassword = async (
  code: string,
  newPassword: string
): Promise<ApiResponse<{ status: 'success' }>> => {
  try {
    await confirmPasswordReset(auth, code, newPassword);
    return { data: { status: 'success' } };
  } catch (error) {
    console.error('Error resetting password:', error);
    return { error: 'Failed to reset password. The link may have expired.' };
  }
};

export default {
  getUser,
  getUserCompanies,
  getUserPrimaryCompany,
  updateUserProfile,
  updateLastLogin,
  hasCompanyAccess,
};
